/**
 * Full-screen product detail page — replaces the popup modal for standard
 * Marketplace items. Route: /marketplace/product/:slug
 *
 * Data sources (in priority order):
 *   1. The exported PRODUCTS list from MarketplacePage.jsx
 *   2. Live MicroCare pricing / variants merged in via fetchMicrocarePricing()
 *
 * If the slug isn't found, we render a friendly "not found" screen with
 * a link back to the marketplace catalog.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ShoppingCart,
  CheckCircle2,
  ExternalLink,
  FileText,
  Download,
  Package,
  Shield,
  Clock,
  Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SEO, { productSchema } from "@/components/SEO";
import { PRODUCTS } from "@/pages/MarketplacePage";
import { fetchMicrocarePricing } from "@/data/sterileProcessingProducts";
import { useQuoteCart } from "@/contexts/QuoteCartContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────
// Coming-Soon reservation block  (renders in place of Add-to-Cart)
// ─────────────────────────────────────────────────────────────────
function ComingSoonBlock({ product }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/coming-soon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          product_name: product.name,
          email,
          company,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
      toast.success("You're on the list — we'll notify you at launch.");
    } catch (err) {
      // Even if the endpoint isn't up yet, don't block the UX.
      console.error("coming-soon signup failed", err);
      setSubmitted(true);
      toast.success("Thanks — we've saved your interest.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="coming-soon-block">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-amber-600" />
        <span className="text-xs font-bold uppercase tracking-widest text-amber-700">
          Coming Soon
          {product.launch_target ? ` · ${product.launch_target}` : ""}
        </span>
      </div>
      <p className="text-sm text-slate-700 mb-4 leading-relaxed">
        This product is in production. Reserve your stock now and we&apos;ll email you the moment it ships.
        Distributors and specifying agents — download the artwork &amp; dieline below to preview.
      </p>
      {submitted ? (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-800 text-sm">
          <CheckCircle2 className="w-5 h-5" />
          Thanks — we&apos;ll notify you at launch.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <input
            type="email"
            required
            placeholder="Work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm"
            data-testid="coming-soon-email"
          />
          <input
            type="text"
            placeholder="Hospital / company (optional)"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
            className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 text-sm"
            data-testid="coming-soon-company"
          />
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-base shadow"
            data-testid="coming-soon-submit"
          >
            <Bell className="w-5 h-5 mr-2" />
            {submitting ? "Saving…" : "Notify me when available"}
          </Button>
        </form>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Downloads block  (manufacturer artwork, dielines, sales sheets)
// ─────────────────────────────────────────────────────────────────
function DownloadsBlock({ downloads }) {
  if (!downloads?.length) return null;
  const primary = downloads.find((d) => d.primary);
  const secondary = downloads.filter((d) => !d.primary);
  const abs = (u) => (u.startsWith("http") ? u : `${API_URL}${u}`);

  return (
    <div className="mb-6 space-y-3" data-testid="downloads-block">
      {/* Hero download — dieline / manufacturer file */}
      {primary && (
        <a
          href={abs(primary.url)}
          target="_blank"
          rel="noreferrer"
          className="group flex items-stretch overflow-hidden rounded-xl border-2 border-cyan-500 bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg hover:shadow-xl transition-shadow"
          data-testid={`download-${primary.key}`}
        >
          <div className="p-4 flex items-center justify-center bg-white/15 border-r border-white/20">
            <FileText className="w-8 h-8" />
          </div>
          <div className="flex-1 p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-100">
              For Your Box Manufacturer
            </div>
            <div className="text-base font-bold leading-tight mt-0.5">
              {primary.label}
            </div>
            <div className="text-xs text-cyan-50 mt-1">
              Cut lines · fold scores · dimensions · perforation · substrate spec
            </div>
          </div>
          <div className="flex items-center pr-4">
            <div className="flex items-center gap-1.5 rounded-full bg-white/20 group-hover:bg-white/30 px-3 py-2 text-sm font-bold transition-colors">
              <Download className="w-4 h-4" />
              PDF
            </div>
          </div>
        </a>
      )}

      {/* Secondary color-variant artwork */}
      {secondary.length > 0 && (
        <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50/50 overflow-hidden">
          <div className="bg-cyan-100/80 px-4 py-3 border-b border-cyan-200 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-700" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-cyan-900">
              Print-Ready Box Artwork
            </h2>
            <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-cyan-700">
              {secondary.length} files
            </span>
          </div>
          <ul className="divide-y divide-cyan-100">
            {secondary.map((d) => (
              <li key={d.key}>
                <a
                  href={abs(d.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-cyan-100/40 transition-colors"
                  data-testid={`download-${d.key}`}
                >
                  <Download className="w-4 h-4 text-cyan-700 shrink-0" />
                  <span className="flex-1 text-sm text-slate-800 font-medium">{d.label}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function MarketplaceProductPage() {
  const { slug } = useParams();
  const { addItem, setShowCart } = useQuoteCart();
  const [microcarePricing, setMicrocarePricing] = useState({});
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Pull live MicroCare pricing (case + variants) so SPD items match the grid
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bySlug = await fetchMicrocarePricing(API_URL);
        if (!cancelled) setMicrocarePricing(bySlug);
      } catch (e) {
        console.error("MicroCare pricing merge failed on detail page", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Look up the base product from the static catalog and merge in pricing
  const product = useMemo(() => {
    const base = PRODUCTS.find((p) => p.id === slug);
    if (!base) return null;
    const enrich = microcarePricing[base.id];
    if (!enrich || !enrich.default_variant) return base;
    const dv = enrich.default_variant;
    return {
      ...base,
      customer_price: dv.case_price ?? base.customer_price,
      unit_price: dv.unit_price ?? base.unit_price,
      case_price: dv.case_price ?? base.case_price,
      pack: dv.pack ?? base.pack,
      variants: enrich.variants,
    };
  }, [slug, microcarePricing]);

  // When a variant list exists, default-select the cheapest available one
  useEffect(() => {
    if (product?.variants?.length && !selectedVariant) {
      const dv =
        product.variants.find((v) => v.is_default)
        || product.variants.find((v) => v.available !== false)
        || product.variants[0];
      setSelectedVariant(dv);
    }
  }, [product, selectedVariant]);

  // Scroll to top + reset gallery whenever the slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImage(0);
    setSelectedVariant(null);
    setQty(1);
  }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <Package className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2" data-testid="product-not-found-heading">
            Product not found
          </h1>
          <p className="text-slate-500 mb-6">
            We couldn&apos;t find a marketplace item for <span className="font-mono">{slug}</span>.
          </p>
          <Link to="/marketplace">
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="back-to-marketplace-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Marketplace
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const gallery = product.gallery?.length ? product.gallery : [product.image];
  const activeSku = selectedVariant?.sku || product.sku || product.id.toUpperCase();
  const activePack = selectedVariant?.pack || selectedVariant?.title || product.pack || "case";
  const activeUom = selectedVariant?.uom || product.uom || "case";
  // Support two variant shapes: MicroCare {case_price,unit_price} and DMS {price,cost}
  const activePrice = selectedVariant?.case_price
    ?? selectedVariant?.price
    ?? product.customer_price
    ?? product.case_price
    ?? 0;
  const activeUnitPrice = selectedVariant?.unit_price ?? product.unit_price ?? 0;

  const handleAdd = () => {
    const erp = `${product.id}${selectedVariant ? `--${selectedVariant.sku}` : ""}`;
    addItem(
      {
        erp,
        id: erp,
        name: `${product.name}${selectedVariant?.size ? ` — ${selectedVariant.size}` : ""}`,
        description: product.description,
        image: product.image,
        brand: product.brand,
        sku: activeSku,
        pack: activePack,
        uom: activeUom,
        customer_price: activePrice,
        unit_price: activeUnitPrice,
      },
      qty
    );
    setShowCart(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="marketplace-product-page">
      <SEO
        title={`${product.name} — ${product.brand || "Sterile Mate"} · In Stock`}
        description={`${product.name} — ${product.brand ? product.brand + " · " : ""}${product.tagline || product.description || ""}. In stock at Sterile Mate — Houston, TX. Serving TX / LA / AL / MS.`}
        canonical={`/marketplace/product/${product.id}`}
        image={product.image}
        type="product"
        keywords={[
          product.name,
          product.brand,
          `${product.brand || ""} ${product.name || ""} in stock`.trim(),
          product.subcategory,
        ].filter(Boolean)}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            productSchema({
              name: product.name,
              sku: activeSku,
              brand: product.brand,
              description: product.description || product.tagline,
              image: product.image,
              url: `/marketplace/product/${product.id}`,
              price: activePrice > 0 ? activePrice : null,
              availability: product.coming_soon
                ? "https://schema.org/PreOrder"
                : "https://schema.org/InStock",
            }),
            ...(product.faqs?.length
              ? [{
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: product.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }]
              : []),
          ],
        }}
      />
      <SiteHeader />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 text-sm text-slate-500 flex-wrap">
          <Link to="/marketplace" className="hover:text-emerald-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Marketplace
          </Link>
          <span className="text-slate-300">/</span>
          <Link
            to={`/marketplace?category=${encodeURIComponent(product.category || "")}`}
            className="hover:text-emerald-700 capitalize"
          >
            {(product.category || "").replace(/-/g, " ")}
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-800 font-semibold line-clamp-1">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-2 gap-10">
        {/* LEFT — Gallery */}
        <div>
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 overflow-hidden">
            <img
              src={gallery[activeImage]}
              alt={product.name}
              className="w-full h-[420px] md:h-[520px] object-contain p-8"
              data-testid="product-detail-hero-image"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-6 gap-2">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`rounded-lg overflow-hidden border-2 transition-all ${
                    i === activeImage
                      ? "border-emerald-500 shadow"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`product-detail-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-16 object-contain bg-white" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Info */}
        <div>
          {product.brand && (
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">
              {product.brand}
            </p>
          )}
          <h1
            className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-3"
            data-testid="product-detail-title"
          >
            {product.name}
          </h1>
          {product.tagline && (
            <p className="text-lg text-slate-600 mb-4">{product.tagline}</p>
          )}

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {product.badge && (
              <Badge className="bg-amber-500 text-slate-900 font-semibold">{product.badge}</Badge>
            )}
            {product.coming_soon ? (
              <Badge className="bg-rose-100 text-rose-800 border border-rose-200">
                <Clock className="w-3 h-3 mr-1" />
                Coming Soon{product.launch_target ? ` · ${product.launch_target}` : ""}
              </Badge>
            ) : (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                In Stock
              </Badge>
            )}
            <span className="text-xs text-slate-500">
              SKU <span className="font-mono font-semibold text-slate-800">{activeSku}</span>
            </span>
          </div>

          {/* Price + Pack picker */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6">
            <div className="flex items-baseline gap-3 mb-4 flex-wrap">
              {activePrice > 0 ? (
                <>
                  <span className="text-4xl font-black text-slate-900" data-testid="product-detail-price">
                    ${Number(activePrice).toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                    / {activeUom}
                  </span>
                  {activeUnitPrice > 0 && Number(activeUnitPrice) !== Number(activePrice) && (
                    <span className="ml-2 text-sm text-slate-500">
                      (${Number(activeUnitPrice).toFixed(2)} per unit)
                    </span>
                  )}
                </>
              ) : (
                <span className="text-slate-500 italic">Request pricing on quote</span>
              )}
            </div>

            {product.variants?.length > 1 && (
              <div className="mb-5">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                  Size / Pack option ({product.variants.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
                  {product.variants.map((v) => {
                    const active = selectedVariant?.sku === v.sku;
                    const label = v.pack || v.title || v.size || v.uom;
                    const price = v.case_price ?? v.price ?? 0;
                    const unavail = v.available === false;
                    return (
                      <button
                        key={v.sku}
                        onClick={() => !unavail && setSelectedVariant(v)}
                        disabled={unavail}
                        className={`text-left rounded-lg border p-3 text-xs transition-all ${
                          active
                            ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                            : unavail
                              ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed"
                              : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`variant-${v.sku}`}
                      >
                        <div className="font-semibold leading-tight">{label}</div>
                        <div className="font-mono text-[10px] text-slate-500 mt-0.5">{v.sku}</div>
                        {price > 0 && (
                          <div className="text-sm font-bold text-slate-800 mt-1">
                            ${Number(price).toFixed(2)}
                            {unavail && <span className="text-[10px] text-rose-600 font-normal ml-1">· sold out</span>}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Qty + Add-to-cart  (OR Coming Soon reservation form) */}
            {product.coming_soon ? (
              <ComingSoonBlock product={product} />
            ) : (
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={qty}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setQty(Number.isFinite(n) && n >= 1 ? n : 1);
                  }}
                  onFocus={(e) => e.target.select()}
                  className="w-20 h-11 text-center text-base font-bold border border-slate-300 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                  aria-label={`Quantity for ${product.name}`}
                  data-testid="product-detail-qty"
                />
                <Button
                  onClick={handleAdd}
                  className="flex-1 h-11 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-base shadow"
                  data-testid="product-detail-add-to-cart"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Quote Cart
                </Button>
              </div>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div className="mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
                Overview
              </h2>
              <p className="text-slate-700 leading-relaxed">{product.description}</p>
            </div>
          )}

          {/* Features */}
          {product.features?.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
                Features
              </h2>
              <ul className="grid sm:grid-cols-2 gap-2">
                {product.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Specs */}
          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">
                  Specifications
                </h2>
              </div>
              <dl className="divide-y divide-slate-100">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 gap-4 px-4 py-2.5 text-sm">
                    <dt className="text-slate-500 col-span-1">{k}</dt>
                    <dd className="text-slate-800 font-medium col-span-2">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Downloads (manufacturer artwork, dielines, spec sheets) */}
          <DownloadsBlock downloads={product.downloads} />

          {/* Applications */}
          {product.applications?.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
                Applications
              </h2>
              <div className="flex flex-wrap gap-2">
                {product.applications.map((a, i) => (
                  <span
                    key={i}
                    className="text-xs px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Extra links */}
          <div className="flex flex-wrap gap-3">
            {product.brochureUrl && (
              <a
                href={product.brochureUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
                data-testid="product-detail-brochure"
              >
                <Download className="w-4 h-4" /> {product.brochureLabel || "Download brochure"}
              </a>
            )}
            {product.productUrl && (
              <a
                href={product.productUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 hover:underline"
                data-testid="product-detail-vendor-link"
              >
                <ExternalLink className="w-4 h-4" /> Manufacturer page
              </a>
            )}
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline ml-auto"
              data-testid="product-detail-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Marketplace
            </Link>
          </div>

          {/* Trust footer */}
          <div className="mt-8 grid grid-cols-3 gap-2 text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            <div className="rounded-lg bg-white border border-slate-200 p-3 flex flex-col items-center gap-1">
              <Shield className="w-4 h-4 text-emerald-600" />
              Net-30 Terms
            </div>
            <div className="rounded-lg bg-white border border-slate-200 p-3 flex flex-col items-center gap-1">
              <FileText className="w-4 h-4 text-emerald-600" />
              Official Quote
            </div>
            <div className="rounded-lg bg-white border border-slate-200 p-3 flex flex-col items-center gap-1">
              <Package className="w-4 h-4 text-emerald-600" />
              Ships from TX
            </div>
          </div>
        </div>
      </div>

      {/* ─────────── Product FAQ (rendered when product.faqs is present) ─────────── */}
      {product.faqs?.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-16" data-testid="product-faq-section">
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">
            Frequently asked questions
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            The five things buyers ask most before requesting a quote on {product.brand || product.name}.
          </p>
          <div className="space-y-3">
            {product.faqs.map((f, i) => (
              <details
                key={f.q}
                className="group border border-slate-200 rounded-lg bg-white overflow-hidden"
                data-testid={`product-faq-${i}`}
              >
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-slate-50">
                  <span className="font-semibold text-slate-900">{f.q}</span>
                  <span className="text-slate-400 group-open:rotate-90 transition-transform">→</span>
                </summary>
                <div className="px-5 pb-5 text-slate-700 leading-relaxed border-t border-slate-100 pt-3">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
